/**
 * Boli Live - Orders Page
 *
 * Order tracking and history.
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package,
  Truck,
  CheckCircle2,
  Clock,
  ShoppingBag,
  ChevronRight,
  Search,
} from 'lucide-react';
import { cn } from '../lib/utils';

type OrderStatus = 'all' | 'pending' | 'shipped' | 'delivered';

const statusFilters: { id: OrderStatus; label: string; icon: React.ElementType }[] = [
  { id: 'all', label: 'All', icon: ShoppingBag },
  { id: 'pending', label: 'Pending', icon: Clock },
  { id: 'shipped', label: 'Shipped', icon: Truck },
  { id: 'delivered', label: 'Delivered', icon: CheckCircle2 },
];

const mockOrders: {
  id: string;
  title: string;
  image: string;
  price: number;
  status: 'pending' | 'shipped' | 'delivered';
  date: string;
}[] = [];

export default function OrdersPage() {
  const [activeFilter, setActiveFilter] = useState<OrderStatus>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredOrders = mockOrders.filter((order) =>
    activeFilter === 'all' ? true : order.status === activeFilter
  );

  return (
    <div className="min-h-screen pb-24 pt-4">
      <div className="px-4 pb-4 bg-background sticky top-0 z-30">
        <h1 className="text-2xl font-bold mb-4 text-foreground">My Orders</h1>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search orders..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-secondary border border-transparent rounded-full pl-11 pr-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:bg-background transition-all"
          />
        </div>

        {/* Status Filters */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
          {statusFilters.map((filter) => (
            <button
              key={filter.id}
              onClick={() => setActiveFilter(filter.id)}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 border',
                activeFilter === filter.id
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background border-border text-muted-foreground hover:bg-secondary'
              )}
            >
              <filter.icon className="w-4 h-4" />
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Orders List */}
      <div className="px-4">
        <AnimatePresence mode="wait">
          {filteredOrders.length > 0 ? (
            <div className="space-y-3">
              {filteredOrders.map((order) => (
                <motion.div
                  key={order.id}
                  className="bg-card border border-border rounded-lg p-4 flex items-center gap-4 shadow-sm"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="w-16 h-16 rounded-lg bg-secondary overflow-hidden">
                    <img
                      src={order.image}
                      alt={order.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate text-foreground">{order.title}</p>
                    <p className="text-sm font-bold text-foreground font-display">
                      PKR {order.price.toLocaleString()}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span
                        className={cn(
                          'text-[10px] px-2 py-0.5 rounded-full font-medium uppercase',
                          order.status === 'pending' && 'bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-400',
                          order.status === 'shipped' && 'bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400',
                          order.status === 'delivered' && 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400'
                        )}
                      >
                        {order.status}
                      </span>
                      <span className="text-xs text-muted-foreground">{order.date}</span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </motion.div>
              ))}
            </div>
          ) : (
            <motion.div
              className="text-center py-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="w-20 h-20 rounded-full bg-secondary flex items-center justify-center mx-auto mb-6">
                <Package className="w-10 h-10 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-foreground">No orders yet</h3>
              <p className="text-muted-foreground text-sm max-w-xs mx-auto mb-6">
                When you win an auction, your orders will appear here
              </p>
              <button
                className="btn-primary inline-flex items-center gap-2"
                onClick={() => window.location.href = '/'}
              >
                <ShoppingBag className="w-4 h-4" />
                Browse Auctions
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
